<template>
  <div>
    <!-- UNLOCK CHATROOM MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'ChatLocked'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-lock"
            :width="91"
            :height="95"
            class="icon"
          />
        </template>
        <template #title>{{ $t(chatLockedCopyKeys.title) }}</template>
        <template #subtitle>{{ $t(chatLockedCopyKeys.subtitle) }}</template>
        <template #body>
          <ul>
            <li>{{ $t(chatLockedCopyKeys.usp1) }}</li>
            <li>{{ $t('modal_chat_locked_usp2') }}</li>
            <li>{{ $t('modal_chat_locked_usp3') }}</li>
            <li>{{ $t('modal_chat_locked_usp4') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="chatlocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_chat_locked_button2') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- SUPERINTRESSE PAYWALL: freemium/trial tapped the star (no balance
         field, or SUPERLIKE_NOT_ALLOWED from the backend). The out-of-balance
         modal (SuperInterestPurchase) is built separately. -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'SuperInterestPaywall'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-lock"
            :width="91"
            :height="95"
            class="icon"
          />
        </template>
        <template #title>{{
          $t('modal_super_interest_paywall_title')
        }}</template>
        <template #subtitle>
          <p class="super-interest-paywall-intro">
            {{ $t('modal_super_interest_paywall_body') }}
          </p>
          <p class="super-interest-paywall-list-title">
            {{ $t('modal_super_interest_paywall_list_title') }}
          </p>
        </template>
        <template #body>
          <ul>
            <li v-for="key in superInterestPaywallUsps" :key="key">
              {{ $t(key) }}
            </li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="superinterestpaywall"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_super_interest_paywall_cta') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- SUPERINTRESSE EXPLAIN: a paying user with Superintresse left taps the
         "N superintressen kvar" pill on Besvarade — a short primer on how to
         use them (prototype). Buying more is offered at balance 0. -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'SuperInterestExplain'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <span class="super-interest-explain-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12 2.5l2.9 6.1 6.6.9-4.8 4.7 1.1 6.6-5.8-3.2-5.8 3.2 1.1-6.6L2.5 9.5l6.6-.9z"
              />
            </svg>
          </span>
        </template>
        <template #title>{{
          $t('modal_super_interest_explain_title')
        }}</template>
        <template #subtitle>{{
          $t('modal_super_interest_explain_body')
        }}</template>
        <template #body>
          <ul>
            <li v-for="key in superInterestExplainUsps" :key="key">
              {{ $t(key) }}
            </li>
          </ul>
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          class="grey smaller full-width"
          @click="setGlobalModal(false)"
          >{{ $t('modal_super_interest_explain_cta') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- FREEMIUM WEEKLY MATCH LIMIT PAYWALL (AB test 'one-match-per-week') -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'WeeklyMatchLimit'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-lock"
            :width="91"
            :height="95"
            class="icon"
          />
        </template>
        <template #title>
          {{ $t('modal_weekly_match_limit_title') }}
        </template>
        <template v-if="weeklyMatchLimitSubtitle" #subtitle>{{
          weeklyMatchLimitSubtitle
        }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_weekly_match_limit_usp1') }}</li>
            <li>{{ $t('modal_weekly_match_limit_usp2') }}</li>
            <li>{{ $t('modal_weekly_match_limit_usp3') }}</li>
            <li>{{ $t('modal_weekly_match_limit_usp4') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="weeklymatchlimit"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_chat_locked_button2') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK CONTACT SWAP MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'ContactSwapLocked'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-lock"
            :width="91"
            :height="95"
            class="icon"
          />
        </template>
        <template #title>{{ $t('swap_view_chat_pay_title') }}</template>
        <template #subtitle>{{ $t('swap_view_chat_pay_subtitle') }}</template>
        <template #body>
          <ul>
            <li>{{ $t('swap_view_chat_pay_list_item1') }}</li>
            <li>{{ $t('swap_view_chat_pay_list_item2') }}</li>
            <li>{{ $t('swap_view_chat_pay_list_item3') }}</li>
            <li>{{ $t('swap_view_chat_pay_list_item4') }}</li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="chattablocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_chat_locked_button2') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK SWAP MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'SwapsLocked'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_swaps_locked_title') }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_swaps_locked_usp1') }}</li>
            <li>{{ $t('modal_swaps_locked_usp2') }}</li>
            <li>{{ $t('modal_swaps_locked_usp3') }}</li>
            <li v-if="$country.isFeatureEnabled('SWAP_APPLICATION')">
              {{ $t('modal_swaps_locked_usp4') }}
            </li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="swapslocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK SWAPS ALT MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'SwapsLockedAlt'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{
          $t('modal_swaps_locked_title_alt', { count: postFilterCountTotal })
        }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_swaps_locked_usp1') }}</li>
            <li>{{ $t('modal_swaps_locked_usp2') }}</li>
            <li>{{ $t('modal_swaps_locked_usp3_alt') }}</li>
            <li v-if="$country.isFeatureEnabled('SWAP_APPLICATION')">
              {{ $t('modal_swaps_locked_usp4') }}
            </li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="swapslocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK ALTERNATIVE MACTHES MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'AlternativeMatchesLocked'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{
          $t('modal_show_alternative_matches_title')
        }}</template>
        <template #subtitle>{{
          $t('modal_show_alternative_matches_subtitle')
        }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_show_alternative_matches_usp1') }}</li>
            <li>{{ $t('modal_show_alternative_matches_usp2') }}</li>
            <li>{{ $t('modal_show_alternative_matches_usp3') }}</li>
            <li>{{ $t('modal_show_alternative_matches_usp4') }}</li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="alternativematcheslocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK MAP MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'MapLocked'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_unlock_map_title') }}</template>
        <template #subtitle>{{ $t('modal_unlock_map_subtitle') }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_unlock_map_usp1') }}</li>
            <li>{{ $t('modal_unlock_map_usp2') }}</li>
            <li>{{ $t('modal_unlock_map_usp3') }}</li>
            <li>{{ $t('modal_unlock_map_usp4') }}</li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="maplocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK UNLIMITED LIKES MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'GetUnlimitedLikes'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_unlimited_title') }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_swaps_locked_usp1') }}</li>
            <li>{{ $t('modal_swaps_locked_usp2') }}</li>
            <li>{{ $t('modal_swaps_locked_usp3') }}</li>
            <li v-if="$country.isFeatureEnabled('SWAP_APPLICATION')">
              {{ $t('modal_swaps_locked_usp4') }}
            </li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="unlimitedlikes"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- SEE ALL SWAP SUGGESTIONS MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'SeeAllSwapSuggestions'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_see_all_title') }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_swaps_locked_usp1') }}</li>
            <li>{{ $t('modal_swaps_locked_usp2') }}</li>
            <li>{{ $t('modal_swaps_locked_usp3') }}</li>
            <li v-if="$country.isFeatureEnabled('SWAP_APPLICATION')">
              {{ $t('modal_swaps_locked_usp4') }}
            </li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="seeallswapsuggestions"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- NO LIKES LEFT MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'NoLikesLeft'"
      @close="handleCloseNoLikesLeft"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="exclamation"
            :width="68"
            :height="75"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_noleft_title') }}</template>
        <template #subtitle>
          <p class="noleft-tomorrow">{{ $t('modal_noleft_tomorrow') }}</p>
          <p>{{ $t('modal_noleft_subtitle') }}</p>
        </template>
        <template #body>
          <ul>
            <li>{{ $t('modal_noleft_usp1') }}</li>
            <li>{{ $t('modal_noleft_usp2') }}</li>
            <li>{{ $t('modal_noleft_usp3') }}</li>
            <li>{{ $t('modal_noleft_usp4') }}</li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="seeallswapsuggestions"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- DAILY NEW REQUEST CHATS LIMIT MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'DailyRequestLimit'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="exclamation"
            :width="68"
            :height="75"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_daily_request_limit_title') }}</template>
        <template #body>
          <p>
            {{
              $t('modal_daily_request_limit_text', {
                limit: DAILY_REQUEST_CHAT_LIMIT
              })
            }}
          </p>
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          class="grey smaller full-width"
          @click="setGlobalModal(false)"
          >{{ $t('modal_daily_request_limit_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- UNLOCK OR ACTIVATE FREEMIUM -->
    <BaseInfoModal
      v-if="showGlobalModal === 'ActivateOrPay'"
      :is-loading="!selectedProduct || isLoading"
      :primary-action-text="
        $t('modal_action_become_member', {
          price: propositionProductPrice
        })
      "
      :primary-action-link="`${$t(
        'path_payment_proposition',
        $routeLocale
      )}/${propositionIdQuery}`"
      :secondary-action-text="
        hideActivateFreemiumOption
          ? ''
          : $t('current_subscription_activate_freemium')
      "
      @secondaryActionClick="handleActivateFreemiumClick(propositionIdQuery)"
      @close="handleActivateOrPayClose()"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="icon-stars"
            :width="72"
            :height="72"
            class="icon"
          />
        </template>
        <template #title>{{ $t('freemium_banner_header') }}</template>
        <template #subtitle>{{ $t('freemium_banner_subtitle') }}</template>
        <template #body>
          <ul>
            <li>{{ $t('freemium_banner_usp1') }}</li>
            <li>{{ $t('freemium_banner_usp2') }}</li>
            <li>{{ $t('freemium_banner_usp3') }}</li>
            <li>{{ $t('freemium_banner_usp4') }}</li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
        </template>
      </FancyModalContentNew>
    </BaseInfoModal>

    <!-- NEW INTEREST MARKS LIMITED MODAL -->
    <BaseInfoModal
      v-if="showGlobalModal === 'NewInterestLimited'"
      :tertiary-action-text="$t('blocked_modal_button')"
      :tertiary-action-link="$t('path_matches')"
      @close="setGlobalModal(false)"
      @tertiary-clicked="chatLinkClicked"
    >
      <FancyModalContent>
        <template #icon>
          <BaseIcon
            icon-file="icon-stars"
            :width="72"
            :height="72"
            class="icon"
          />
        </template>
        <template #title>{{ $t('limited_modal_title') }}</template>
        <template #body>
          <p>
            {{ $t('limited_modal_text') }}
          </p>
        </template>
      </FancyModalContent>
    </BaseInfoModal>

    <!-- PREMIUM REVRESE INTEREST PAYWALL -->
    <PremiumActionModal
      v-if="showGlobalModal === 'ReverseInterestActionPaywall'"
      premium-modal-type="REVERSE_INTEREST"
      @close="setGlobalModal(false)"
    />

    <!-- PREMIUM INTEREST MARKS MODAL -->
    <PremiumActionModal
      v-if="showGlobalModal === 'InterestMarksActionModal'"
      premium-modal-type="INTEREST_MARKS"
      @close="setGlobalModal(false)"
    />

    <!-- PREMIUM NO INTEREST MARKS MODAL -->
    <PremiumActionModal
      v-if="showGlobalModal === 'NoInterestMarksActionModal'"
      premium-modal-type="NO_INTEREST_MARKS"
      @close="setGlobalModal(false)"
    />

    <!-- FULL SWAP ON CLICK MODAL -->
    <FullSwapModalWrapper
      v-if="showGlobalModal === 'FullSwapModal'"
      @close="setGlobalModal(false)"
    />

    <!-- BOOST INFO MODAL -->
    <BoostActiveModal
      v-if="showGlobalModal === 'BoostInfo' && showBoostActiveModal"
      :end-time="boostEndsAt"
      @close="setGlobalModal(false)"
    />
    <BoostExpiredModal
      v-else-if="showGlobalModal === 'BoostInfo' && showBoostExpiredModal"
      @close="setGlobalModal(false)"
    />
    <StandardModal
      v-else-if="showGlobalModal === 'BoostInfo'"
      standard-modal-name="BoostInfoModal"
      @close="setGlobalModal(false)"
    >
      <BoostInfoContent slot="content" />
    </StandardModal>

    <!-- SUPERINTRESSE PURCHASE (design 1c): the star tapped with an empty
         balance, or SUPERLIKE_NO_REMAINING from the backend. Same sheet as
         the boost panel; locked while a payment redirect (?q=) completes. -->
    <StandardModal
      v-if="showGlobalModal === 'SuperInterestPurchase'"
      standard-modal-name="SuperInterestModal"
      :show-header="false"
      :no-shadow="true"
      :locked="!!$route.query.q"
      @close="setGlobalModal(false)"
    >
      <SuperInterest slot="content" />
    </StandardModal>

    <!-- UNLOCK MY VISITORS MODAL -->
    <BaseInfoModalNew
      v-if="showGlobalModal === 'MyVisitorsLocked'"
      @close="setGlobalModal(false)"
    >
      <FancyModalContentNew>
        <template #icon>
          <BaseIcon
            icon-file="illustration-stars"
            :width="72"
            :height="98"
            class="icon"
          />
        </template>
        <template #title>{{ $t('modal_unlock_my_visitors_title') }}</template>
        <template #subtitle>{{
          $t('modal_unlock_my_visitors_subtitle')
        }}</template>
        <template #body>
          <ul>
            <li>{{ $t('modal_unlock_my_visitors_usp1') }}</li>
            <li>{{ $t('modal_unlock_my_visitors_usp2') }}</li>
            <li>{{ $t('modal_unlock_my_visitors_usp3') }}</li>
            <li>{{ $t('modal_unlock_my_visitors_usp4') }}</li>
            <li>{{ $t('premium_usp_message_first') }}</li>
          </ul>
          <TrustBox
            v-if="$country.isFeatureEnabled('TRUSTPILOT')"
            key="swapslocked"
            type="Micro Trustscore"
            style="margin-top: 25px"
          />
        </template>
      </FancyModalContentNew>
      <template #actions>
        <BaseButton
          :link="`${$t(
            'path_payment_proposition',
            $routeLocale
          )}/${primaryPropositionId}`"
          icon-file="premium-icon"
          class="grey smaller full-width"
          >{{ $t('modal_swaps_locked_button') }}</BaseButton
        >
      </template>
    </BaseInfoModalNew>

    <!-- REPORT MODAL -->
    <ReportModalWrapper
      v-if="showGlobalModal === 'ReportModal'"
      @close="setGlobalModal(false)"
    />
    <SortingModalWrapper
      v-if="showGlobalModal === 'SortingModal'"
      @close="setGlobalModal(false)"
    />

    <BaseInfoModalNew
      v-if="showGlobalModal === 'RatingModal'"
      @close="setGlobalModal(false)"
    >
      <RatingTrustpilot @close="setGlobalModal(false)" />
    </BaseInfoModalNew>

    <!-- COMPLETE LISTING MODAL -->
    <CompleteListingModal
      v-if="
        showGlobalModal === 'CompleteListingModal' &&
        primaryProposition &&
        completeListingModalData
      "
      :proposition="primaryProposition"
      :summary="completeListingModalData.summary"
      :recent-visitors="completeListingModalData.recentVisitors"
      @close="setGlobalModal(false)"
    />

    <EndMatchConfirmModal v-if="endMatchConfirm.show" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FancyModalContent from '@/components/Modals/ModalContentTemplates/FancyModalContent';
import FancyModalContentNew from '@/components/Modals/ModalContentTemplates/FancyModalContentNew';
import RatingTrustpilot from '@/components/Modals/ModalContentTemplates/RatingTrustpilot.vue';
import StandardModal from '@/components/Modals/Standard';
import BoostInfoContent from '@/components/Modals/BoostInfoContent';
import SuperInterestPanelLoading from '@/components/Payments/SuperInterestPanelLoading.vue';
import SuperInterestPanelError from '@/components/Payments/SuperInterestPanelError.vue';
import asyncPanel from '@/utils/asyncPanel';
import BoostActiveModal from '@/components/Modals/BoostActiveModal.vue';
import BoostExpiredModal from '@/components/Modals/BoostExpiredModal.vue';
import TrustBox from '@/components/TrustBox/TrustBox.vue';
import { formatCurrency } from '@/filters/currency';
import { remainingWaitParts, timeTicker } from '@/utils/helpers/relativeTime';
import { boostEndTime } from '@/utils/helpers/reboostWindow';
import { WEEKLY_UNLOCK_MAX_DAYS } from '@/utils/helpers/weeklyUnlock';
import { log } from '../utils/logger';
import events from '@/utils/helpers/events';
import ReportModalWrapper from '@/components/Proposition/Report/ReportModalWrapper';
import SortingModalWrapper from '@/components/Filter/SortingModalWrapper';
import PremiumActionModal from '@/components/Modals/PremiumActionModal';
import FullSwapModalWrapper from '@/components/FullSwap/FullSwapModalWrapper.vue';
import CompleteListingModal from '@/components/Onboarding/CompleteListingModal.vue';
import EndMatchConfirmModal from '@/components/Modals/EndMatchConfirmModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseIcon from '@/components/BaseIcon.vue';
import BaseInfoModal from '@/components/BaseInfoModal.vue';
import BaseInfoModalNew from '@/components/BaseInfoModalNew.vue';

export default {
  name: 'GlobalModalsContainer',

  components: {
    FancyModalContent,
    FancyModalContentNew,
    RatingTrustpilot,
    StandardModal,
    BoostInfoContent,
    // Async: only rendered inside the SuperInterestPurchase sheet, and pulls
    // in @adyen (BasePayment → Adyen) — keeps adyen out of the initial bundle,
    // like ExtraVisibility in App.vue. The sheet has no header and the panel's
    // own spinner ships inside the chunk, so asyncPanel supplies the stand-ins
    // that keep it from opening as an empty white box (see utils/asyncPanel).
    SuperInterest: asyncPanel(
      () =>
        import(
          /* webpackChunkName: "super-interest" */ '@/components/Payments/SuperInterest'
        ),
      {
        loading: SuperInterestPanelLoading,
        error: SuperInterestPanelError
      }
    ),
    BoostActiveModal,
    BoostExpiredModal,
    TrustBox,
    ReportModalWrapper,
    SortingModalWrapper,
    PremiumActionModal,
    FullSwapModalWrapper,
    CompleteListingModal,
    EndMatchConfirmModal,
    BaseButton,
    BaseIcon,
    BaseInfoModal,
    BaseInfoModalNew
  },

  data() {
    return {
      isLoading: false,
      // Must match the backend's daily cap on new request chats
      DAILY_REQUEST_CHAT_LIMIT: 30,
      superInterestPaywallUsps: [
        'modal_super_interest_paywall_usp1',
        'modal_super_interest_paywall_usp2',
        'modal_super_interest_paywall_usp3'
      ],
      superInterestExplainUsps: [
        'modal_super_interest_explain_usp1',
        'modal_super_interest_explain_usp2'
      ]
    };
  },

  computed: {
    ...mapGetters({
      showGlobalModal: 'ui/showGlobalModal',
      selectedProduct: 'klarnaPayments/getSelectedProduct',
      primaryPropositionId: 'myPropositions/primaryPropositionId',
      primaryProposition: 'myPropositions/primaryProposition',
      products: 'klarnaPayments/getProducts',
      appReady: 'app/ready',
      postFilterCountTotal: 'swapList/postFilterCountTotal',
      endMatchConfirm: 'ui/endMatchConfirm',
      completeListingModalData: 'ui/completeListingModalData',
      lockAllFreemiumChats: 'myPropositions/lockAllFreemiumChats',
      unlockMatchAvailableAt: 'matches/unlockMatchAvailableAt',
      isTestEnabled: 'app/isTestEnabled',
      inReboostWindow: 'extraVisibility/inReboostWindow'
    }),

    consumablesEnabled() {
      return this.isTestEnabled('consumables');
    },

    // When the primary ad's boost ends (ms), null without a boost.
    // extraVisibility.endDate is the instant it ends, as the backend sends
    // it — the boost is counted down to that time, and nothing here moves
    // it to the end of the day. The app reads the same timestamp in
    // getBoostSummary, so the two count down to the same moment.
    boostEndsAt() {
      return boostEndTime(this.primaryProposition?.extraVisibility?.endDate);
    },

    // Consumables A/B: the variant never sees the statistics sheet.
    // 'BoostInfo' opens the compact status card while the boost runs and
    // the result panel otherwise — the boost has expired, or the ad no
    // longer carries it (the backend drops extraVisibility after expiry,
    // so /boost-summary links arrive without boost data). Reads the
    // ticking clock so the card gives way to the panel when the boost ends.
    // The ad can still carry a boost that has ended: an open re-boost
    // window (read from /api/boost-summary) always means the panel.
    showBoostActiveModal() {
      return (
        this.consumablesEnabled &&
        !this.inReboostWindow &&
        this.boostEndsAt !== null &&
        this.boostEndsAt > timeTicker.now
      );
    },

    showBoostExpiredModal() {
      return this.consumablesEnabled && !this.showBoostActiveModal;
    },

    // AB test 'one-match-per-week': the WeeklyMatchLimit paywall
    // subtitle, counted down from the summary's unlock date — days when
    // at least a day remains, hours below that (rounded up, never 0).
    // Empty while no summary has answered or no cooldown runs, and the
    // template withholds the subtitle then.
    weeklyMatchLimitSubtitle() {
      if (this.unlockMatchAvailableAt === undefined) return '';
      const wait = remainingWaitParts(
        this.unlockMatchAvailableAt,
        timeTicker.now,
        { maxDays: WEEKLY_UNLOCK_MAX_DAYS }
      );
      if (!wait) return '';
      if (wait.unit === 'hour') {
        return this.$tc('modal_weekly_match_limit_subtitle_hours', wait.value, {
          hours: wait.value
        });
      }
      return this.$tc('modal_weekly_match_limit_subtitle', wait.value, {
        days: wait.value
      });
    },

    // AB test 'lock-all-chats': every chat is locked for the test group, so
    // the "message first" framing doesn't apply — reuse the copy that was in
    // production before freemium could answer chats. USP 2-4 and the CTA are
    // the same in both variants.
    chatLockedCopyKeys() {
      return this.lockAllFreemiumChats
        ? {
            title: 'modal_chat_locked_all_title',
            subtitle: 'modal_chat_locked_all_subtitle',
            usp1: 'modal_chat_locked_all_usp1'
          }
        : {
            title: 'modal_chat_locked_title',
            subtitle: 'modal_chat_locked_subtitle',
            usp1: 'modal_chat_locked_usp1'
          };
    },

    chatRoomId() {
      return this.$route.query.chatRoomId;
    },

    propositionIdQuery() {
      if (this.$route.query.propositionId) {
        return parseInt(this.$route.query.propositionId);
      }

      return null;
    },

    // returning-inactive-users A/B: the variant removes the free-activation
    // option from the ActivateOrPay modal, leaving only the paid path.
    // Evaluated lazily (only when the modal renders) so users are enrolled
    // in the experiment at the moment they actually see it.
    hideActivateFreemiumOption() {
      return this.$growthbook.isFeatureFlagEnabled('returning-inactive-users');
    },

    propositionProductPrice() {
      if (!this.products) {
        return '';
      }

      const product = this.products.find(
        x => x.category === 'annonsforlangning' && x.quantity === 30
      );
      if (!product) {
        return '';
      }

      return this.formatCurrency(product.priceInclVat);
    }
  },

  methods: {
    ...mapActions({
      setGlobalModal: 'ui/setGlobalModal',
      activateFreemium: 'handleSubscription/activateFreemium',
      setToast: 'toast/setToast',
      refreshPropositions: 'myPropositions/refreshPropositions'
    }),

    formatCurrency,

    handleCloseNoLikesLeft() {
      this.setGlobalModal(false);
    },

    async handleActivateFreemiumClick(propositionId) {
      this.isLoading = true;

      try {
        await this.activateFreemium(propositionId);
        this.setToast({
          title: this.$t('confirmation_proposition_title')
        });
        this.refreshPropositions();
      } catch (error) {
        log(`activateFreemium from inactivated: ${error}`, true);
        this.setToast({
          title: this.$t('toast_general_error_message')
        });
      }

      this.setGlobalModal(false);
    },

    handleActivateOrPayClose() {
      // Only clear query if chatRoomId is present
      if (this.$route.query.propositionId) {
        this.$router.replace({ query: {} });
      }

      this.setGlobalModal(false);
    },

    chatLinkClicked() {
      events.emitEvent(events.eventTypes.CLICK, 'Interest Limitation Chat', {
        src: 'Interest Limitation Modal'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
/* The no-likes-left subtitle is two stacked lines ("Och du får nya imorgon"
   over "Skaffa Premium idag och få:") — kill the default p margins so they
   read as one block inside FancyModalContentNew's subtitle spacing. */
.noleft-tomorrow,
.noleft-tomorrow + p {
  margin: 0;
}

/* Paywall subtitle: the intro, then "Skaffa Premium och få:" leading into
   the perk list. */
.super-interest-paywall-intro {
  margin: 0;
}

.super-interest-paywall-list-title {
  margin: 16px 0 0;
  color: var(--Text-Primary);
}

/* Same footprint as the paywall's lock illustration. */
.super-interest-explain-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 91px;
  height: 91px;
  color: $superlike-pink;
}

.super-interest-explain-icon svg {
  width: 72px;
  height: 72px;
  display: block;
}
</style>
